import imgWelcomeStarIcon from '../../images/welcome-star-icon.svg';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';
import imgWelcomeSadIcon from '../../images/welcome-sad-icon.svg';
import { ICON_CLOSE } from '../../js/game-icons';

export const WELCOME_CONFIG = {
    icon: imgWelcomeStarIcon,
    title: 'Witaj w Beauty Reveal',
    description: '<p>Na ekranie pojawi się rozmyty obraz produktu, który z czasem będzie stawał się coraz wyraźniejszy.</p>' +
        '<p>Twoim zadaniem jest jak najszybsze rozpoznanie produktu i wybranie poprawnej odpowiedzi spośród 4 opcji. Uważaj jednak – im szybciej odpowiesz, tym więcej punktów możesz zdobyć!</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol>' +
            '<li>Obserwuj rozmyty obraz produktu, który stopniowo się wyostrza.</li>' +
            '<li>Wybierz poprawną nazwę produktu spośród 4 odpowiedzi.</li>' +
            '<li>Odpowiadaj jak najszybciej – gra składa się z 5 rund!</li>' +
            '</ol>' +
            '<p>Sprawdź, jak szybko rozpoznasz produkt! 🔥</p>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ Punkty zdobywasz za poprawną odpowiedź.</li>' +
            '<li>⚡ Im szybciej rozpoznasz produkt, tym więcej punktów otrzymasz.</li>' +
            '<li>❌ Błędna odpowiedź lub brak odpowiedzi oznacza 0 punktów za rundę.</li>' +
            '</ul>' +
            '<p>Maksymalny wynik to 25 punktów</p>',
    },
};

let state = null;
let raf = null;
let timers = [];
let currentModal = null;

const PIXEL_MIN = 0.024;
const PIXEL_MAX = 0.6;
const CANVAS_SIZE = 400;

const ICON_CHECK = `<svg class="bi-opt__icon bi-opt__icon--check" viewBox="0 0 61 49" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M58.5623 1.20967C61.2616 3.22901 61.8111 7.05423 59.7894 9.75233L32.3629 46.3518C30.1359 49.3155 25.8021 49.6255 23.1778 47.0067L1.79234 25.6928C-0.592503 23.3136 -0.59821 19.4518 1.77985 17.067C4.16016 14.6742 8.03095 14.6685 10.4181 17.0545L26.8125 33.4011L50.0208 2.43623C52.1207 -0.286889 55.9505 -0.772771 58.5634 1.20912L58.5623 1.20967Z" fill="currentColor"/></svg>`;
const ICON_CROSS = `<svg class="bi-opt__icon bi-opt__icon--cross" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M24.3987 15.8419L10.4119 1.85506V1.85622C7.7466 -0.683016 4.35824 -0.553618 1.78609 1.85622C-0.595362 4.23787 -0.595362 8.09942 1.78609 10.482L15.7729 24.4688L1.78609 38.4557C-0.595362 40.8373 -0.595362 44.6988 1.78609 47.0814C4.16774 49.4629 8.02928 49.4629 10.4119 47.0814L24.3987 33.0946L38.3855 47.0814C40.7671 49.4629 44.6287 49.4629 47.0113 47.0814C49.3927 44.6998 49.3927 40.8382 47.0113 38.4557L33.0245 24.4688L47.0113 10.4808C49.3927 8.0992 49.3927 4.23764 47.0113 1.85506C44.4457 -0.683046 40.5047 -0.291422 38.3855 1.85506L24.3987 15.8419Z" fill="currentColor"/></svg>`;

function contentEl(modal) {
    return modal.querySelector('.game-modal__content');
}

function escHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}

function setHtml(modal, html) {
    const el = contentEl(modal);
    if (el) el.innerHTML = html;
}

function clearTimers() {
    timers.forEach((t) => clearTimeout(t));
    timers = [];
    if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
    }
}

async function fetchStart(taskId) {
    const res = await fetch(`/api/task/${taskId}/blurred-image/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
    });
    if (res.status === 409) {
        const err = await res.json();
        throw new Error(err?.error || 'already_completed');
    }
    if (!res.ok) throw new Error(`start_failed_${res.status}`);
    return res.json();
}

async function fetchRound(taskId, token, sessionToken, elapsedMs) {
    const res = await fetch(`/api/task/${taskId}/blurred-image/round`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ token, sessionToken, elapsedMs }),
    });
    if (!res.ok) throw new Error(`round_failed_${res.status}`);
    return res.json();
}

async function fetchAnswer(taskId, token, chosenIndex, sessionToken, elapsedMs) {
    const res = await fetch(`/api/task/${taskId}/blurred-image/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ token, chosenIndex, sessionToken, elapsedMs }),
    });
    if (!res.ok) throw new Error(`answer_failed_${res.status}`);
    return res.json();
}

async function fetchSubmit(taskId, sessionToken) {
    const res = await fetch(`/api/task/${taskId}/blurred-image/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ sessionToken }),
    });
    return res.json();
}

function loadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image_load_failed'));
        img.src = url;
    });
}

function drawPixelated(ctx, offCtx, img, factor) {
    const S = CANVAS_SIZE;
    ctx.clearRect(0, 0, S, S);

    if (factor >= 0.999) {
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(img, 0, 0, S, S);
        return;
    }

    const small = Math.max(2, Math.floor(S * factor));
    offCtx.clearRect(0, 0, S, S);
    offCtx.imageSmoothingEnabled = true;
    offCtx.drawImage(img, 0, 0, small, small);

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(offCtx.canvas, 0, 0, small, small, 0, 0, S, S);
}

function renderRoundShell(modal) {
    const K = state.rounds.length;
    const i = state.currentRound;

    const opts = state.rounds[i].options.map((label, idx) => `
        <button class="bi-opt" data-bi-idx="${idx}" type="button" disabled>
            <span class="bi-opt__label">${escHtml(label)}</span>
            ${ICON_CHECK}
            ${ICON_CROSS}
        </button>
    `).join('');

    setHtml(modal, `
        <div class="bi-game">
            <div class="bi-hud">
                <div class="bi-hud__pill">
                    <span class="bi-hud__pill-label">Punkty:</span>
                    <span class="bi-hud__pill-value" id="bi-points">${state.totalRanking}</span>
                </div>
                <div class="bi-hud__right">
                    <span class="bi-hud__round">${i + 1}/${K}</span>
                    <button type="button" class="bi-hud__close js-game-modal-close" aria-label="Zamknij grę">${ICON_CLOSE}</button>
                </div>
            </div>
            <div class="bi-stage">
                <div class="bi-timer" id="bi-timer">--:--</div>
                <div class="bi-canvas-wrap">
                    <canvas id="bi-canvas" width="${CANVAS_SIZE}" height="${CANVAS_SIZE}"></canvas>
                </div>
                <div class="bi-options" id="bi-options">${opts}</div>
            </div>
        </div>
    `);

    const options = modal.querySelector('#bi-options');
    if (options) {
        Array.from(options.querySelectorAll('.bi-opt')).forEach((btn) => {
            btn.addEventListener('click', () => onOptionClick(modal, Number(btn.dataset.biIdx)));
        });
    }
}

async function runRound(modal) {
    const i = state.currentRound;
    const round = state.rounds[i];

    renderRoundShell(modal);

    let imageUrl;
    try {
        const elapsedMs = state.gameStartTs ? Math.floor(Date.now() - state.gameStartTs) : 0;
        const data = await fetchRound(state.taskId, round.token, state.sessionToken, elapsedMs);
        imageUrl = data.imageUrl;
    } catch {
        advanceRound(modal);
        return;
    }
    if (!state || state.currentRound !== i) return;

    let img;
    try {
        img = await loadImage(imageUrl);
    } catch {
        advanceRound(modal);
        return;
    }
    if (!state || state.currentRound !== i) return;

    const canvas = modal.querySelector('#bi-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const offscreen = document.createElement('canvas');
    offscreen.width = CANVAS_SIZE;
    offscreen.height = CANVAS_SIZE;
    const offCtx = offscreen.getContext('2d');

    drawPixelated(ctx, offCtx, img, PIXEL_MIN);

    timers.push(setTimeout(() => {
        if (!state || state.currentRound !== i) return;
        modal.querySelectorAll('.bi-opt').forEach((btn) => { btn.disabled = false; });
    }, 350));

    const durationMs = state.data.durationSeconds * 1000;
    const start = performance.now();
    state.roundStart = start;

    const timerEl = modal.querySelector('#bi-timer');

    function tick(now) {
        if (!state || state.currentRound !== i) return;
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / durationMs);
        const factor = PIXEL_MIN + (PIXEL_MAX - PIXEL_MIN) * progress;

        drawPixelated(ctx, offCtx, img, factor);

        if (timerEl) {
            const remainingMs = Math.max(0, durationMs - elapsed);
            const totalSec = Math.ceil(remainingMs / 1000);
            const mm = Math.floor(totalSec / 60);
            const ss = totalSec % 60;
            timerEl.textContent = mm + ':' + String(ss).padStart(2, '0');
            const remaining = 1 - progress;
            timerEl.classList.toggle('is-urgent', remaining < 0.2);
        }

        if (progress >= 1) {
            onTimeout(modal);
            return;
        }
        raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
}

async function onOptionClick(modal, idx) {
    if (state.locked) return;
    state.locked = true;

    const i = state.currentRound;
    const round = state.rounds[i];

    if (raf) { cancelAnimationFrame(raf); raf = null; }
    modal.querySelectorAll('.bi-opt').forEach((btn) => { btn.disabled = true; });

    const elapsedMs = Math.floor(performance.now() - state.roundStart);
    let result = null;
    try {
        result = await fetchAnswer(state.taskId, round.token, idx, state.sessionToken, elapsedMs);
    } catch {
        result = { correct: false, correctIndex: null, rankingPointsEarned: 0, redeemablePointsEarned: 0 };
    }
    if (!state || state.currentRound !== i) return;

    if (result.correct) {
        state.correctCount++;
    }
    const earned = Number(result.rankingPointsEarned) || 0;
    state.totalRanking += earned;

    const pointsEl = modal.querySelector('#bi-points');
    if (pointsEl) pointsEl.textContent = String(state.totalRanking);

    const chosenBtn = modal.querySelector(`.bi-opt[data-bi-idx="${idx}"]`);
    if (chosenBtn) {
        chosenBtn.classList.add(result.correct ? 'bi-opt--correct' : 'bi-opt--wrong');
    }

    timers.push(setTimeout(() => advanceRound(modal), 900));
}

async function onTimeout(modal) {
    if (state.locked) return;
    state.locked = true;

    const i = state.currentRound;
    const round = state.rounds[i];

    if (raf) { cancelAnimationFrame(raf); raf = null; }
    modal.querySelectorAll('.bi-opt').forEach((btn) => { btn.disabled = true; });

    const elapsedMs = Math.floor(performance.now() - state.roundStart);
    let result = null;
    try {
        result = await fetchAnswer(state.taskId, round.token, null, state.sessionToken, elapsedMs);
    } catch {
        result = { correct: false, correctIndex: null };
    }
    if (!state || state.currentRound !== i) return;

    const correctLabel = (result.correctIndex != null && round.options[result.correctIndex])
        ? round.options[result.correctIndex]
        : '';

    showTimeoutOverlay(modal, correctLabel);
}

function showTimeoutOverlay(modal, correctLabel) {
    const game = modal.querySelector('.bi-game');
    if (!game) { advanceRound(modal); return; }

    const overlay = document.createElement('div');
    overlay.className = 'bi-modal';
    overlay.innerHTML = `
        <div class="bi-modal__shell">
            <div class="bi-modal__card">
                <button type="button" class="bi-modal__close js-bi-modal-continue" aria-label="Zamknij">${ICON_CLOSE}</button>
                <h2 class="bi-modal__title">Za wolno tym razem</h2>
                <p class="bi-modal__subtitle">To był <strong>${escHtml(correctLabel.toLowerCase())}</strong>.</p>
                <p class="bi-modal__points">+0 PKT</p>
                <div class="bi-modal__icon"><img src="${imgWelcomeSadIcon}" alt=""></div>
            </div>
            <button type="button" class="bi-modal__continue js-bi-modal-continue">DALEJ</button>
        </div>
    `;

    overlay.addEventListener('click', (e) => {
        if (e.target.closest('.js-bi-modal-continue')) {
            overlay.remove();
            advanceRound(modal);
        }
    });

    game.appendChild(overlay);
}

function advanceRound(modal) {
    state.currentRound++;
    state.locked = false;
    if (state.currentRound >= state.rounds.length) {
        submitAndShowResults(modal);
        return;
    }
    runRound(modal);
}

async function submitAndShowResults(modal) {
    setHtml(modal, `<div class="bi-game"><div class="bi-pending">Zapisywanie wyniku…</div></div>`);
    let result = null;
    try {
        result = await fetchSubmit(state.taskId, state.sessionToken);
    } catch {
        result = { reason: 'network_error' };
    }
    if (state && state.onComplete) state.onComplete(result);
    document.dispatchEvent(new CustomEvent('game-modal:result-show', {
        detail: {
            rankingPoints: result.totalRankingPoints ?? result.rankingPointsEarned ?? 0,
            redeemablePoints: result.totalRedeemablePoints ?? result.redeemablePointsEarned ?? 0,
        },
    }));
}

export async function renderBlurredImage(modal, data, onComplete) {
    clearTimers();

    modal.classList.add('game-modal--blurred-image');

    const taskId = data?.task?.id;
    if (!taskId) {
        setHtml(modal, `<div class="bi-game"><div class="bi-error">Brak ID zadania.</div></div>`);
        return;
    }

    setHtml(modal, `<div class="bi-game"><div class="bi-pending">Ładowanie gry…</div></div>`);

    let startData = null;
    try {
        startData = await fetchStart(taskId);
    } catch (e) {
        const m = String(e?.message);
        if (m === 'already_completed' || m === 'game_in_progress') {
            renderGameMessage(modal, startBlockedMessage(m));
        } else {
            setHtml(modal, `<div class="bi-game"><div class="bi-error">Nie udało się wczytać gry.</div></div>`);
        }
        return;
    }

    state = {
        taskId,
        sessionToken: startData.sessionToken,
        data: startData,
        rounds: startData.rounds || [],
        currentRound: 0,
        correctCount: 0,
        totalRanking: 0,
        roundStart: 0,
        locked: false,
        onComplete: onComplete || null,
        gameStartTs: Date.now(),
    };
    currentModal = modal;

    runRound(modal);
}

export async function forceEndBlurredImage() {
    if (!state) return;

    const modal = currentModal;
    const taskId = state.taskId;
    const sessionToken = state.sessionToken;
    const onComplete = state.onComplete;

    clearTimers();
    state = null;
    currentModal = null;

    if (modal) {
        setHtml(modal, `<div class="bi-game"><div class="bi-pending">Wysyłanie wyniku…</div></div>`);
    }

    let result = null;
    try {
        const res = await fetch(`/api/task/${taskId}/blurred-image/abandon`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ sessionToken }),
        });
        result = await res.json();
    } catch {
        result = { totalRankingPoints: 0, totalRedeemablePoints: 0 };
    }

    if (onComplete) onComplete(result);
    document.dispatchEvent(new CustomEvent('game-modal:result-show', {
        detail: {
            rankingPoints: result.totalRankingPoints ?? 0,
            redeemablePoints: result.totalRedeemablePoints ?? 0,
        },
    }));
}

export function teardownBlurredImage(modal) {
    clearTimers();
    if (modal) modal.classList.remove('game-modal--blurred-image');
    state = null;
    currentModal = null;
}
